import { useState, useEffect, useRef } from "react";
import { X, Loader2, Check, MessageSquare } from "lucide-react";
import { analytics } from "../lib/analytics";

interface FeedbackDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8787";
const MAX_LENGTH = 1000;

export function FeedbackDialog({ isOpen, onClose }: FeedbackDialogProps) {
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Focus textarea when dialog opens
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      // Delay reset to allow close animation
      const timeout = setTimeout(() => {
        setMessage("");
        setIsSuccess(false);
        setError(null);
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen && !isSubmitting) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, isSubmitting, onClose]);

  const handleSubmit = async () => {
    if (!message.trim() || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: message.trim() }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({ error: "Unknown error" }));
        throw new Error(data.error || "Failed to send feedback");
      }

      analytics.feedbackSubmitted(message.length);
      setIsSuccess(true);
      
      // Auto-close after success
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      console.error("Feedback error:", err);
      setError(err instanceof Error ? err.message : "Failed to send feedback");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const charsRemaining = MAX_LENGTH - message.length;
  const isOverLimit = charsRemaining < 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={!isSubmitting ? onClose : undefined}
      />

      {/* Dialog */}
      <div className="relative w-full max-w-md glass-strong rounded-2xl p-6 animate-in">
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          disabled={isSubmitting}
          className="absolute top-4 right-4 p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors disabled:opacity-50"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-xl bg-accent/20 text-accent">
            <MessageSquare className="w-5 h-5" />
          </div>
          <h2 className="text-lg font-semibold text-foreground">Share Feedback</h2>
        </div>

        {/* Description */}
        <p className="text-sm text-muted-foreground mb-4">
          Help improve Pick Three AI. Your feedback matters.
        </p>

        {isSuccess ? (
          /* Success state */
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <div className="p-3 rounded-full bg-green-500/20 text-green-400">
              <Check className="w-6 h-6" />
            </div>
            <p className="text-foreground font-medium">Thanks for your feedback!</p>
          </div>
        ) : (
          <>
            {/* Textarea */}
            <div className="relative">
              <textarea
                ref={textareaRef}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="What's on your mind?"
                disabled={isSubmitting}
                rows={5}
                className={`
                  w-full px-4 py-3 rounded-xl
                  bg-white/5 border border-white/10
                  text-foreground placeholder:text-muted-foreground
                  focus:outline-none focus:border-accent/50 focus:ring-1 focus:ring-accent/30
                  disabled:opacity-50 disabled:cursor-not-allowed
                  resize-none transition-colors
                  ${isOverLimit ? "border-red-500/50 focus:border-red-500/50 focus:ring-red-500/30" : ""}
                `}
              />
              
              {/* Character count */}
              <div className={`absolute bottom-3 right-3 text-xs ${
                isOverLimit ? "text-red-400" : charsRemaining < 100 ? "text-yellow-400" : "text-muted-foreground"
              }`}>
                {charsRemaining}
              </div>
            </div>

            {/* Error message */}
            {error && (
              <p className="mt-2 text-sm text-red-400">{error}</p>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 mt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!message.trim() || isOverLimit || isSubmitting}
                className="
                  flex items-center gap-2 px-5 py-2 rounded-xl
                  text-sm font-semibold
                  bg-accent text-accent-foreground
                  hover:bg-accent-hover
                  disabled:opacity-40 disabled:cursor-not-allowed
                  transition-all duration-200
                "
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Send Feedback"
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

